import { yupResolver } from "@hookform/resolvers/yup";
import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import * as yup from "yup";
import CButton from "../../components/CButton";
import CFormProvider from "../../components/HookFormComponents/CFormProvider";
import { CFormSelect } from "../../components/HookFormComponents/CFormSelect.js";
import CFormTextField from "../../components/HookFormComponents/CFormTextField";
import CFormCheckbox from "../../components/HookFormComponents/CFormCheckbox";
import CFormRadioGroup from "../../components/HookFormComponents/CFormRadioGroup";
import { supabase } from "../../config/supabaseClient";
import {
  TRAINING_CATEGORY_OPTIONS,
  NQF_LEVEL_OPTIONS,
  NQF_ALIGNED_OPTIONS,
  COST_PER_LEARNER_LIMIT,
  PROGRAM_DURATION_OPTIONS,
  roleType,
} from "../../constant/constants";
import { TRAINING_CATALOGUE_PLACEHOLDERS } from "../../constant/placeholders";
import { fetchSetaTemplateByCompanyId } from "../../slices/setaTemplateSlice";
import { fetchTrainingCatalogues } from "../../slices/trainingCatalogueSlice";
import { messages } from "../../constant/alertMessages.js";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CloseIcon from "@mui/icons-material/Close";
import CDialog from "../../components/CDialog";
import { isCompanyPrivileged } from "../../utils/roleUtils";

function AddTrainingCatalogue() {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const localUser = JSON.parse(localStorage.getItem("sdfUser"));
  const companyId = localUser?.company_id;
  const setaFields = useSelector((state) => state.setaTemplate.setaTemplate);
  const [categoryName, setCategoryName] = useState("");
  const [isEditMode, setIsEditMode] = useState(false);
  const [trainingId, setTrainingId] = useState(null);
  const [isSetaCompliant, setIsSetaCompliant] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("CompanyCatalogue");
  const [trainingCategoryDialogState, setTrainingCategoryDialogState] =
    useState({ show: false });
  const [companyCatalogs, setCompanyCatalogs] = useState([]);
  const [selectedCatalogId, setSelectedCatalogId] = useState(null);

  useEffect(() => {
    const fetchCategoryName = async () => {
      const categoryId = location.state?.categoryId;
      if (categoryId) {
        const { data, error } = await supabase
          .from("training_catalogue")
          .select("name, sita_complaint")
          .eq("id", categoryId)
          .single();

        if (data) {
          setCategoryName(data.name);
          setIsSetaCompliant(data.sita_complaint);
        }
      }
    };

    // Check if we're in edit mode
    if (location.state?.editMode && location.state?.trainingData) {
      setIsEditMode(true);
      setTrainingId(location.state.trainingId);
    }

    // Set active tab from location state
    if (location.state?.activeTab) {
      setActiveTab(location.state.activeTab);
    }

    if (companyId) {
      dispatch(fetchSetaTemplateByCompanyId(companyId));

      // Fetch company catalogs if coming from Assign Critical Training page or Requested Training
      if (
        location.state?.returnToCriticalSkills ||
        location.state?.returnToRequestedCatalogue
      ) {
        const loadCatalogs = async () => {
          try {
            const result = await dispatch(fetchTrainingCatalogues(companyId));
            if (result.payload) {
              // Filter only company catalogs (company_id is not null)
              const companyCats = result.payload.filter(
                (cat) => cat.company_id !== null
              );
              setCompanyCatalogs(companyCats);
            }
          } catch (error) {
            console.error("Error loading company catalogs:", error);
          }
        };
        loadCatalogs();
      }
    }

    fetchCategoryName();
  }, [location.state, companyId, dispatch]);

  // Create default values based on current location state
  const getDefaultValues = () => ({
    trainingName: location.state?.trainingData?.trainingName || "",
    programDuration: location.state?.trainingData?.programDuration || "",
    trainingCategory: location.state?.trainingData?.trainingCategory || "",
    targetedSkills: location.state?.trainingData?.targetedSkills || "",
    trainingCostPerHead:
      location.state?.trainingData?.trainingCostPerHead || "",
    trainingProviderName:
      location.state?.trainingData?.trainingProviderName || "",
    trainingDescription:
      location.state?.trainingData?.trainingDescription || "",
    nqf_aligned: location.state?.trainingData?.nqf_aligned || "",
    nqf_level: location.state?.trainingData?.nqf_level || "",
    trainingUrl: location.state?.trainingData?.trainingUrl || "",
    // Set is_critical to true by default if coming from "Add New Training" in Critical Skills tab
    is_critical: location.state?.returnToCriticalSkills
      ? true
      : location.state?.trainingData?.is_critical || false,
    selectedCatalog: location.state?.categoryId || "",
  });

  // Define SETA compliance configuration
  const SETA_COMPLIANCE_FIELDS = {
    nqf_aligned: {
      field: "nqf_aligned",
      label: "NQF Aligned",
      validation: {
        required: true,
        message: "NQF Aligned is required for SETA compliant training",
      },
      options: NQF_ALIGNED_OPTIONS,
    },
    nqf_level: {
      field: "nqf_level",
      label: "NQF Level",
      validation: {
        required: true,
        message: "NQF Level is required for SETA compliant training",
      },
      options: NQF_LEVEL_OPTIONS,
    },
  };

  // Define validation schema
  const validationSchema = yup.object().shape({
    trainingName: yup
      .string()
      .required("Training name is required")
      .min(3, "Training name must be at least 3 characters"),
    programDuration: yup.string().when("$activeTab", {
      is: "CompanyCatalogue",
      then: (schema) => schema.required("Program duration is required"),
      otherwise: (schema) => schema.optional(),
    }),

    selectedCatalog: yup
      .string()
      .when("$returnToCriticalSkills", {
        is: true,
        then: (schema) => schema.required("Please select a catalog"),
        otherwise: (schema) => schema.optional(),
      })
      .when("$returnToRequestedCatalogue", {
        is: true,
        then: (schema) => schema.required("Please select a catalog"),
        otherwise: (schema) => schema.optional(),
      }),
    targetedSkills: yup.string().required("Targeted skills is required"),
    trainingCostPerHead: yup
      .number()
      .typeError("Cost per learner must be a number")
      .required("Cost per learner is required")
      .min(0, "Cost per learner cannot be negative")
      .max(
        COST_PER_LEARNER_LIMIT,
        messages.COST_PER_LEARNER_LIMIT_MESSAGE(COST_PER_LEARNER_LIMIT)
      )
      .test(
        "is-decimal",
        "Cost per learner can have up to 2 decimal places",
        (value) => !value || /^\d+(\.\d{1,2})?$/.test(value)
      ),
    trainingProviderName: yup
      .string()
      .required("Training provider name is required"),
    trainingDescription: yup
      .string()
      .required("Training description is required")
      .min(10, "Training Details must be at least 10 characters"),
    nqf_aligned: yup.string().required("NQF Aligned is required"),
    nqf_level: yup.string().when("nqf_aligned", {
      is: (val) => val === "Yes",
      then: (schema) =>
        schema.required("NQF Level is required when NQF Aligned is Yes"),
      otherwise: (schema) => schema.optional().transform(() => ""),
    }),
    trainingUrl: yup
      .string()
      .optional()
      .nullable()
      .test(
        "is-url",
        "Training URL must be a valid URL",
        (value) => !value || value === "" || yup.string().url().isValidSync(value)
      )
      .transform((value) => (value === "" ? null : value)),
  });

  const methods = useForm({
    defaultValues: getDefaultValues(),
    resolver: yupResolver(validationSchema),
    mode: "onChange",
    context: {
      isSetaCompliant,
      activeTab,
      returnToCriticalSkills: location.state?.returnToCriticalSkills || false,
      returnToRequestedCatalogue:
        location.state?.returnToRequestedCatalogue || false,
    },
  });

  const {
    handleSubmit,
    watch,
    reset,
    setValue,
    formState: { errors },
  } = methods;

  // Reset form when location state changes (for edit mode or when coming from Critical Skills or Requested Training)
  useEffect(() => {
    if (
      location.state?.trainingData ||
      location.state?.returnToCriticalSkills ||
      location.state?.returnToRequestedCatalogue
    ) {
      const newDefaultValues = getDefaultValues();
      reset(newDefaultValues);
    }
  }, [
    location.state?.trainingData,
    location.state?.returnToCriticalSkills,
    location.state?.returnToRequestedCatalogue,
    reset,
  ]);

  // Watch for NQF Aligned changes and clear NQF Level when set to "No"
  useEffect(() => {
    const nqfAlignedValue = watch("nqf_aligned");
    if (nqfAlignedValue === "No") {
      setValue("nqf_level", "");
    }
  }, [watch("nqf_aligned"), setValue]);

  // Watch selectedCatalog to update state
  const watchedSelectedCatalog = watch("selectedCatalog");
  useEffect(() => {
    if (watchedSelectedCatalog) {
      setSelectedCatalogId(watchedSelectedCatalog);
    }
  }, [watchedSelectedCatalog]);

  const onSubmit = async (values) => {
    // Prevent multiple submissions
    if (isLoading) {
      return;
    }

    setIsLoading(true);
    try {
      if (!localUser?.company_id && activeTab === "CompanyCatalogue") {
        toast.error("Company ID is required");
        setIsLoading(false);
        return;
      }

      // Use selected catalog from form if coming from Assign Critical Training or Requested Training, otherwise use location state
      const categoryId =
        location.state?.returnToCriticalSkills ||
        location.state?.returnToRequestedCatalogue
          ? values.selectedCatalog || selectedCatalogId
          : location.state?.categoryId;

      if (!categoryId) {
        toast.error("No category selected");
        setIsLoading(false);
        return;
      }

      // Convert programDuration id to value for database storage
      let programDurationValue = null;
      if (values.programDuration) {
        const matchingOption = PROGRAM_DURATION_OPTIONS.find(
          (option) => option.id === values.programDuration
        );
        programDurationValue = matchingOption
          ? matchingOption.value
          : values.programDuration;
      }

      // Create base training data
      const trainingData = {
        company_id:
          activeTab === "IFundiCatalogue" ? null : localUser.company_id,
        catalogue_id: categoryId,
        name: values.trainingName,
        // type: activeTab === "CompanyCatalogue" ? values.programDuration : null,
        program_duration: programDurationValue,
        training_category:
          activeTab === "IFundiCatalogue" ? values.trainingCategory : null,
        cost: parseFloat(values.trainingCostPerHead) || 0,
        provider: values.trainingProviderName,
        description: values.trainingDescription,
        targeted_skills: values.targetedSkills,
        url: values.trainingUrl || null,
        status: "active",
        // Set is_critical to true if coming from "Add New Training" in Critical Skills tab, otherwise use form value
        is_critical:
          activeTab === "CompanyCatalogue"
            ? location.state?.returnToCriticalSkills
              ? true
              : values.is_critical || false
            : false,
      };

      // Add SETA compliance fields
      Object.entries(SETA_COMPLIANCE_FIELDS).forEach(([fieldName, config]) => {
        let value = values[fieldName];
        if (fieldName === "nqf_aligned") {
          if (value === "Yes") value = true;
          else value = false;
        }
        trainingData[config.field] = value || (value === false ? false : null);
      });

      let response;
      if (isEditMode && trainingId) {
        // Update existing training
        response = await supabase
          .from("training_catalogue_data")
          .update(trainingData)
          .eq("id", trainingId)
          .select();
      } else {
        // Insert new training
        response = await supabase
          .from("training_catalogue_data")
          .insert(trainingData)
          .select();
      }

      if (response.error) {
        throw response.error;
      }

      // Only show toast if not returning to RequestedCatalogue (it will show its own message)
      if (!location.state?.returnToRequestedCatalogue) {
        toast.success(
          isEditMode
            ? "Training updated successfully!"
            : "Training added successfully!"
        );
      }

      // Call onSuccess callback if provided
      if (location.state?.onSuccess) {
        location.state.onSuccess();
      }

      // Navigate back based on where we came from
      if (location.state?.returnToCriticalSkills) {
        // Return to Assign Critical Training page and refresh trainings
        navigate("/assign-critical-training", {
          state: {
            refreshTrainings: true,
          },
        });
      } else if (location.state?.returnToRequestedCatalogue) {
        // Update training_requests.assigned field to true when training is added to catalogue
        if (
          location.state?.rowData?.trainingRequestIds &&
          location.state.rowData.trainingRequestIds.length > 0
        ) {
          try {
            // Get company_id for filtering
            if (!companyId) {
              console.error(
                "❌ [Training Requests Update] Company ID not found"
              );
              toast.warning(
                "Training added but failed to update request status: Company ID missing"
              );
              navigate("/requested-training", {
                state: {
                  trainingAdded: true,
                  rowData: location.state.rowData,
                },
              });
              return;
            }

            // Get training request IDs
            const trainingRequestIds =
              location.state.rowData.trainingRequestIds;

            const { error: updateError } = await supabase
              .from("training_requests")
              .update({
                status: "processed", // Update status to processed
                updated_at: new Date().toISOString(),
              })
              .in("id", trainingRequestIds)
              .eq("company_id", companyId);

            if (updateError) {
              console.error("Error updating training requests:", updateError.message);
              toast.error(
                "Error updating training request status: " + updateError.message
              );
            }
          } catch (error) {
            console.error("Error updating training requests:", error.message);
            toast.error(
              "Error updating training request status: " + error.message
            );
          }
        }

        // Return to Requested Training page and mark training as added
        navigate("/requested-training", {
          state: {
            trainingAdded: true,
            rowData: location.state.rowData,
            requestCount: location.state?.rowData?.trainingRequestIds?.length || 0,
          },
        });
      } else {
        // Navigate back to the appropriate catalogue page with the category ID to trigger grid refresh
        const targetRoute =
          location.state?.activeTab === "CompanyCatalogue"
            ? "/company-catalogue"
            : location.state?.activeTab === "IFundiCatalogue"
            ? "/ifundi-catalogue"
            : "/training-catalogue";

        navigate(targetRoute, {
          state: {
            refreshGrid: true,
            categoryId: categoryId,
          },
        });
      }
    } catch (error) {
      console.error("Error saving training data:", error);
      const errorMsg =
        error?.message || error?.error || "Error saving training data";
      toast.error(errorMsg);
      setIsLoading(false);
      return;
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    // If coming from Critical Skills, navigate back to Assign Critical Training page
    if (location.state?.returnToCriticalSkills) {
      navigate("/assign-critical-training", {
        state: { refreshTrainings: true },
      });
    } else if (location.state?.returnToRequestedCatalogue) {
      navigate("/requested-training");
    } else if (location.state?.activeTab === "CriticalSkills") {
      navigate("/critical-skills");
    } else if (location.state?.activeTab === "CompanyCatalogue") {
      navigate("/company-catalogue");
    } else if (location.state?.activeTab === "IFundiCatalogue") {
      navigate("/ifundi-catalogue");
    } else {
      // Default fallback - try to determine from activeTab state
      if (activeTab === "CompanyCatalogue") {
        navigate("/company-catalogue");
      } else if (activeTab === "IFundiCatalogue") {
        navigate("/ifundi-catalogue");
      } else {
        // Default fallback - redirect to company catalogue for SDF, ifundi for admin
        const localUser = JSON.parse(localStorage.getItem("sdfUser"));
        const userRole = localUser?.role;
        const defaultRoute =
          isCompanyPrivileged(userRole)
            ? "/company-catalogue"
            : "/ifundi-catalogue";
        navigate(defaultRoute);
      }
    }
  };

  // Helper function to render SETA compliance fields
  const renderSetaComplianceFields = () => {
    const nqfAlignedValue = watch("nqf_aligned");

    return Object.entries(SETA_COMPLIANCE_FIELDS).map(([fieldName, config]) => {
      // Disable NQF Level field when NQF Aligned is "No"
      const isDisabled = fieldName === "nqf_level" && nqfAlignedValue === "No";

      return (
        <CFormSelect
          key={fieldName}
          name={fieldName}
          label={`${config.label}`}
          className="2xl:!mt-0 !mt-0  add_seta_inputs"
          placeholder={
            fieldName === "nqf_aligned"
              ? TRAINING_CATALOGUE_PLACEHOLDERS.nqfAligned
              : TRAINING_CATALOGUE_PLACEHOLDERS.nqfLevel
          }
          required={
            isSetaCompliant && fieldName === "nqf_level"
              ? nqfAlignedValue === "Yes"
              : isSetaCompliant
          }
          options={config.options || []}
          disabled={isDisabled}
        />
      );
    });
  };

  return (
    <div>
      <h2 className="text-3xl leading-3xl text-blue-500 font-semibold mb-4">
        {isEditMode
          ? `Edit Training of '${categoryName}' Catalogue`
          : location.state?.returnToCriticalSkills ||
            location.state?.returnToRequestedCatalogue
          ? "Add Training"
          : `Add Training to '${categoryName}' Catalogue`}
        {isSetaCompliant && (
          <span className="text-sm text-blue-500 ml-2">(SETA Compliant)</span>
        )}
      </h2>
      <div className="card-bg  border border-solid border-blue-200 overflow-hidden 2xl:rounded-2xl rounded-xl bg-white 2xl:h-[calc(100vh-164px)] h-[calc(100vh-148px)]">
        <CFormProvider
          methods={methods}
          onSubmit={handleSubmit(onSubmit)}
          className="custom-input-group  overflow-y-auto  relative h-full flex flex-col z-10"
        >
         <div className="flex-1 lg:p-6 p-4 lg:pb-4 pb-4">

         <div className="grid 2xl:grid-cols-4 xl:grid-cols-3 grid-cols-2 gap-4 relative z-[100] type-category-select">
            {/* Show Select Catalog field when coming from Assign Critical Training page or Requested Training */}
            {(location.state?.returnToCriticalSkills ||
              location.state?.returnToRequestedCatalogue) &&
              activeTab === "CompanyCatalogue" && (
                <CFormSelect
                  key="selectedCatalog"
                  name="selectedCatalog"
                  label="Select Catalog"
                  className="2xl:!mt-0 !mt-0"
                  placeholder={TRAINING_CATALOGUE_PLACEHOLDERS.selectedCatalog}
                  options={companyCatalogs.map((catalog) => ({
                    id: catalog.id,
                    value: catalog.name, // Display name in dropdown
                    label: catalog.name,
                  }))}
                  onChange={(e) => {
                    setSelectedCatalogId(e.target.value);
                    setValue("selectedCatalog", e.target.value);
                  }}
                />
              )}
            <CFormTextField
              name="trainingName"
              label="Training Name"
              className="2xl:!mt-0 !mt-0"
              placeholder={TRAINING_CATALOGUE_PLACEHOLDERS.trainingName}
            />

            <CFormSelect
              key="programDuration"
              name="programDuration"
              label="Program Duration"
              forceShrink={true}
              className="2xl:!mt-0 !mt-0"
              placeholder={TRAINING_CATALOGUE_PLACEHOLDERS.programDuration}
              options={PROGRAM_DURATION_OPTIONS}
            />

            <CFormTextField
              name="trainingProviderName"
              label="Provider"
              className="2xl:!mt-0 !mt-0"
              placeholder={TRAINING_CATALOGUE_PLACEHOLDERS.trainingProviderName}
            />
            <CFormTextField
              name="trainingCostPerHead"
              label="Cost per Learner"
              className="2xl:!mt-0 !mt-0"
              placeholder={TRAINING_CATALOGUE_PLACEHOLDERS.trainingCostPerHead}
            />
            <CFormTextField
              name="targetedSkills"
              label="Targeted Skills"
              className="2xl:!mt-0 !mt-0"
              placeholder={TRAINING_CATALOGUE_PLACEHOLDERS.targetedSkills}
            />
            {renderSetaComplianceFields()}
            {activeTab === "CompanyCatalogue" && (
              <CFormRadioGroup
                name="is_critical"
                label="Is Critical?"
                row={true}
                className="2xl:!mt-0 !mt-0"
                disabled={location.state?.returnToCriticalSkills || false}
                options={[
                  { value: true, label: "Yes" },
                  { value: false, label: "No" },
                ]}
              />
            )}
          </div>

          {/* Training Details field - Full width below other fields */}
          <div className="2xl:!mt-4 !mt-4">
            <CFormTextField
              name="trainingDescription"
              label="Training Details"
              multiline
              rows={10}
              className="w-full !mt-0"
              placeholder={TRAINING_CATALOGUE_PLACEHOLDERS.trainingDescription}
            />
          </div>
          {/* Training URL field - Full width below Training Details */}
          <div className="2xl:!mt-4 !mt-4">
            <CFormTextField
              name="trainingUrl"
              label="Training URL"
              className="w-full !mt-0"
              placeholder={TRAINING_CATALOGUE_PLACEHOLDERS.trainingUrl}
            />
          </div>
         </div>

          {/* <div className="mt-2"></div> */}
          <div className="sticky bottom-0 left-0 w-full px-6 py-3 bg-white z-10 flex gap-3">
            <CButton
              type="submit"
              disabled={isLoading}
              className={`bg-blue-500 text-white hover:bg-blue-700 border border-blue-500 hover:border-blue-700 ${
                isLoading ? "opacity-50 cursor-not-allowed" : ""
              }`}
            >
              {isLoading ? (
                <span className="flex items-center justify-center">
                  <span className="mr-2">Saving...</span>
                  <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                      fill="none"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                </span>
              ) : (
                "Save"
              )}
            </CButton>
            <CButton
              type="button"
              onClick={handleCancel}
              className="transparent text-blue-500 hover:bg-blue-500 hover:text-white border border-blue-500"
            >
              Cancel
            </CButton>
          </div>
        </CFormProvider>
        <CDialog
          open={trainingCategoryDialogState.show}
          onClose={() => setTrainingCategoryDialogState({ show: false })}
        >
          <div className="">
            <div className="flex items-center justify-between mb-2">
              <p className="2xl:text-xl 2xl:leading-xl text-base leading-base text-grey-700 font-semibold ">
                Training Type Category Matrix
              </p>
              <CButton
                onClick={() => setTrainingCategoryDialogState({ show: false })}
                className="!h-auto !p-0"
              >
                <CloseIcon className="text-gray-700 hover:text-gray-900" />
              </CButton>
            </div>
            <div className="border border-blue-200 rounded-xl overflow-hidden">
              <table className="w-full border-collapse text-sm leading-sm">
                <thead>
                  <tr>
                    <th className="text-center p-2 bg-blue-100 font-semibold w-[130px]">
                      Category
                    </th>
                    <th className="text-left p-2 bg-blue-100 font-semibold">
                      Type
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-t border-blue-200 cursor-pointer">
                    <td className="text-center p-2">A</td>
                    <td className="text-left p-2">Bursaries</td>
                  </tr>
                  <tr className="border-t border-blue-200 cursor-pointer">
                    <td className="text-center p-2">B</td>
                    <td className="text-left p-2">Internships</td>
                  </tr>
                  <tr className="border-t border-blue-200 cursor-pointer">
                    <td className="text-center p-2">C</td>
                    <td className="text-left p-2">Learnerships (Workplace)</td>
                  </tr>
                  <tr className="border-t border-blue-200 cursor-pointer">
                    <td className="text-center p-2">D</td>
                    <td className="text-left p-2">
                      Learnerships & Apprenticeships (Institution & Workplace){" "}
                    </td>
                  </tr>
                  <tr className="border-t border-blue-200 cursor-pointer">
                    <td className="text-center p-2">E</td>
                    <td className="text-left p-2">Work-Integrated Learning</td>
                  </tr>
                  <tr className="border-t border-blue-200 cursor-pointer">
                    <td className="text-center p-2">F</td>
                    <td className="text-left p-2">
                      Informal Training (Short Courses, Conferences and other
                      non-credit bearing soft skills)
                    </td>
                  </tr>
                  <tr className="border-t border-blue-200 cursor-pointer">
                    <td className="text-center p-2">G</td>
                    <td className="text-left p-2">
                      Informal Training (Workplace)
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </CDialog>
      </div>
    </div>
  );
}

export default AddTrainingCatalogue;
